import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { INSTAGRAM_URL } from "@/data/content";
import { Reveal, Kicker } from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success(c.form.success);
    } catch (err) {
      setStatus("idle");
      toast.error(c.form.error);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-cream">
      <section className="pt-40 md:pt-52 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Kicker>{c.kicker}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif font-light text-4xl sm:text-6xl md:text-7xl leading-[1.04] mt-6 text-charcoal max-w-4xl">
              {c.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-charcoal/70 font-light text-lg mt-8 max-w-xl leading-relaxed">
              {c.intro}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <a
                href="https://wa.me/351924711317"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 text-xs uppercase tracking-[0.18em] text-cream bg-olive hover:bg-charcoal transition-colors duration-300 px-8 py-4"
              >
                <MessageCircle size={16} strokeWidth={1.5} />
                WhatsApp
              </a>
              <a
                href="mailto:marie.vayacreative@gmail.com"
                data-testid="contact-email-button"
                className="inline-flex items-center justify-center gap-3 text-xs uppercase tracking-[0.18em] text-olive border border-olive hover:bg-olive hover:text-cream transition-colors duration-300 px-8 py-4"
              >
                <Mail size={16} strokeWidth={1.5} />
                Email
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 md:pb-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
            {/* Form */}
            <div className="md:col-span-7">
              {status === "success" ? (
                <Reveal>
                  <div
                    data-testid="contact-success"
                    className="border border-olive/30 bg-cream-2 p-10 md:p-14"
                  >
                    <p className="font-serif text-3xl md:text-4xl text-olive leading-snug">
                      {c.form.success}
                    </p>
                    <button
                      onClick={() => setStatus("idle")}
                      data-testid="contact-reset"
                      className="mt-8 text-xs uppercase tracking-[0.18em] text-olive border-b border-olive pb-1 hover:text-charcoal hover:border-charcoal transition-colors"
                    >
                      {c.subtitle}
                    </button>
                  </div>
                </Reveal>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-taupe">
                        {c.form.name}
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        data-testid="contact-name-input"
                        className="w-full bg-transparent border-b border-taupe/60 py-3 mt-2 text-charcoal placeholder-taupe focus:outline-none focus:border-olive transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-taupe">
                        {c.form.email}
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        className="w-full bg-transparent border-b border-taupe/60 py-3 mt-2 text-charcoal placeholder-taupe focus:outline-none focus:border-olive transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-taupe">
                      {c.form.subject}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      data-testid="contact-subject-input"
                      className="w-full bg-transparent border-b border-taupe/60 py-3 mt-2 text-charcoal placeholder-taupe focus:outline-none focus:border-olive transition-colors"
                    />
                  </div>

                  <div>
                    <label className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-taupe">
                      {c.form.message}
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      data-testid="contact-message-input"
                      className="w-full bg-transparent border-b border-taupe/60 py-3 mt-2 text-charcoal placeholder-taupe focus:outline-none focus:border-olive transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit-button"
                    className="text-xs uppercase tracking-[0.18em] text-cream bg-olive hover:bg-charcoal transition-colors duration-300 px-10 py-4 disabled:opacity-60"
                  >
                    {status === "sending" ? c.form.sending : c.form.submit}
                  </button>
                </form>
              )}
            </div>

            {/* Details */}
            <div className="md:col-span-4 md:col-start-9 space-y-10">
              <div>
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-sage mb-3">
                  {c.details.emailLabel}
                </p>
                <a
                  href={`mailto:${c.details.email}`}
                  data-testid="contact-email-link"
                  className="font-serif text-2xl text-charcoal hover:text-sage transition-colors break-words"
                >
                  {c.details.email}
                </a>
              </div>
              <div>
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-sage mb-3">
                  {c.details.whatsappLabel}
                </p>
                <a
                  href="https://wa.me/351924711317"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="font-serif text-2xl text-charcoal hover:text-sage transition-colors"
                >
                  {c.details.whatsapp}
                </a>
              </div>
              <div>
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-sage mb-3">
                  {c.details.socialLabel}
                </p>
                <div className="flex flex-col gap-2">
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="text-charcoal/75 hover:text-sage transition-colors">Instagram</a>
                  <a href="https://www.linkedin.com/in/byvaya" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="text-charcoal/75 hover:text-sage transition-colors">LinkedIn</a>
                </div>
              </div>
              <div>
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-sage mb-3">
                  {c.details.locationLabel}
                </p>
                <p className="text-charcoal/75">{c.details.location}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
